import { Filter, Info, Search } from 'lucide-react';

import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';

import type { FilterTab } from './constants';

interface PaymentRunReviewFiltersProps {
  filterTab: FilterTab;
  setFilterTab: (tab: FilterTab) => void;
  setTablePage: (page: number | ((p: number) => number)) => void;
  /** Count for the "All" pill (excludes successful card + ACH charges). */
  allFilterCount: number | undefined;
  readyCount: number | undefined;
  flaggedCount: number | undefined;
  removedCount: number | undefined;
  failedCount: number | undefined;
  successfulChargeCount: number | undefined;
  attentionCount: number;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  setAppliedSearch: (v: string) => void;
  onSearch: () => void;
}

export function PaymentRunReviewFilters({
  filterTab,
  setFilterTab,
  setTablePage,
  allFilterCount,
  readyCount,
  flaggedCount,
  removedCount,
  failedCount,
  successfulChargeCount,
  attentionCount,
  searchTerm,
  setSearchTerm,
  setAppliedSearch,
  onSearch,
}: PaymentRunReviewFiltersProps) {
  return (
    <>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <div className='flex flex-wrap items-center gap-2'>
          <Filter className='size-4 text-muted-foreground' />
          <span className='text-sm font-medium text-muted-foreground'>
            Filters
          </span>
          <div className='flex min-w-0 flex-wrap gap-1'>
            {(
              [
                {
                  id: 'all' as const,
                  label: `All (${allFilterCount ?? '—'})`,
                },
                {
                  id: 'ready' as const,
                  label: `Ready (${readyCount ?? '—'})`,
                },
                {
                  id: 'flagged' as const,
                  label: `Flagged (${flaggedCount ?? '—'})`,
                },
                {
                  id: 'removed' as const,
                  label: `Removed (${removedCount ?? '—'})`,
                },
                {
                  id: 'failed' as const,
                  label: `Failed (${failedCount ?? '—'})`,
                },
                {
                  id: 'successfulCharge' as const,
                  label: `Successful Charge (${successfulChargeCount ?? '—'})`,
                },
              ] as const
            ).map(({ id, label }) => (
              <Button
                key={id}
                variant={filterTab === id ? 'default' : 'outline'}
                size='sm'
                className={
                  filterTab === id ? 'bg-amber-500 hover:bg-amber-600' : ''
                }
                onClick={() => {
                  setFilterTab(id);
                  setTablePage(1);
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        <div className='relative w-full min-w-0 max-w-xs'>
          <Search className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            type='search'
            placeholder='Search parent, student, or due date'
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setSearchTerm(v);
              if (v === '') {
                setAppliedSearch('');
                setTablePage(1);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
            className='pl-9'
            aria-label='Search parent, student, or due date'
          />
        </div>
      </div>

      {attentionCount > 0 && (
        <div className='flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm'>
          <Info className='size-5 shrink-0 text-blue-600' />
          <span>
            We found{' '}
            <strong>
              {attentionCount} charge{attentionCount !== 1 ? 's' : ''}
            </strong>{' '}
            that need your attention — look for the <strong>orange</strong> and{' '}
            <strong>red</strong> rows below.
          </span>
        </div>
      )}
    </>
  );
}
